/**
 * Progress Service
 * Handles user progress tracking and weekly activity
 */

import appState from '../core/app-state.js';
import { logEvent } from '../core/event-logger.js';
import logger from '../core/logger.js';
import { calculateClipPerformance, renderSessionSummary } from './performance-service.js';

// Type for clip performance result
interface ClipPerformance {
  correct: number;
  total: number;
  percentage: number;
}



/**
 * Load progress map from localStorage
 */
export function loadProgressMap(): void {
  const stored = localStorage.getItem('listenUpProgress');
  
  if (!stored) {
    appState.progressMap = {};
    appState.progressMap = {};
    return;
  }
  
  try {
    const parsed = JSON.parse(stored);
    
    // Handle migration from array to object format
    if (Array.isArray(parsed)) {
      appState.progressMap = {};
      appState.progressMap = {};
      parsed.forEach((clipId: string) => {
        appState.progressMap[clipId] = true;
        appState.progressMap[clipId] = true;
      });
      // Only save once after migration is complete
      saveProgressMap(appState.progressMap);
    } else if (typeof parsed === 'object' && parsed !== null) {
      appState.progressMap = parsed;
      appState.progressMap = parsed;
    } else {
      appState.progressMap = {};
      appState.progressMap = {};
    }
  } catch (e) {
    // Reset to empty object on corrupt data
    appState.progressMap = {};
    appState.progressMap = {};
  }
}

/**
 * Save progress map to localStorage
 */
export function saveProgressMap(map: Record<string, boolean>): void {
  localStorage.setItem('listenUpProgress', JSON.stringify(map));
  // Make sure both references are in sync
  appState.progressMap = map;
  appState.progressMap = map;
}

/**
 * Check if clip is completed
 */
export function isClipCompleted(clipId: string): boolean {
  return !!appState.progressMap[clipId];
}

/**
 * Get week start date (Monday)
 */
function getWeekStartDate(): string {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek;
  const monday = new Date(now);
  monday.setUTCDate(now.getUTCDate() + diff);
  monday.setUTCHours(0, 0, 0, 0);
  const isoString = monday.toISOString().split('T')[0];
  if (!isoString) throw new Error('Failed to format date');
  return isoString;
}

/**
 * Load weekly activity from localStorage
 */
export function loadWeeklyActivity(): void {
  const weekStart = getWeekStartDate();
  const stored = localStorage.getItem('listenUpWeeklyActivity');
  
  if (!stored) {
    appState.weeklyActivityDays = new Set();
    return;
  }
  
  try {
    const data = JSON.parse(stored);
    
    // Check if we need to reset for new week
    if (data.weekStart !== weekStart) {
      appState.weeklyActivityDays = new Set();
      saveWeeklyActivity();
    } else {
      appState.weeklyActivityDays = new Set(data.days || []);
    }
  } catch (e) {
    appState.weeklyActivityDays = new Set();
  }
}

/**
 * Save weekly activity to localStorage
 */
function saveWeeklyActivity(): void {
  const weekStart = getWeekStartDate();
  const data = {
    weekStart: weekStart,
    days: Array.from(appState.weeklyActivityDays)
  };
  localStorage.setItem('listenUpWeeklyActivity', JSON.stringify(data));
}

/**
 * Record today's activity
 */
export function recordTodayActivity(): void {
  const isoString = new Date().toISOString().split('T')[0];
  if (!isoString) return;
  const today = isoString;
  const hadActivity = appState.weeklyActivityDays.has(today);
  appState.weeklyActivityDays.add(today);
  
  if (!hadActivity) {
    saveWeeklyActivity();
  }
}

/**
 * Mark clip as completed
 * @param clipId - The clip ID to mark as completed
 */
export function markClipCompleted(clipId: string): void {
  if (!clipId) return;
  
  // Check if the clip was already marked as completed
  const wasAlreadyCompleted = appState.progressMap[clipId] === true;
  
  // Mark as completed
  appState.progressMap[clipId] = true;
  appState.progressMap[clipId] = true;
  saveProgressMap(appState.progressMap);
  
  // Only log if this is the first time completing the clip
  if (!wasAlreadyCompleted) {
    // Record today's activity for weekly tracking
    recordTodayActivity();
    
    // Calculate performance statistics for the clip
    const performance = calculateClipPerformance(clipId) as ClipPerformance;
    
    const clip = appState.clips.find(c => c.id === clipId);
    
    // Log clip_complete event with enhanced fields
    logEvent('clip_complete', {
      clipId: clipId,
      totalQuestions: performance.total,
      correctAnswers: performance.correct
    } as any); // Using any for now since the payload has extra fields not in the type definition
    
    // Check if clip is part of a series and log series progress update
    if (clip && clip.storyId) {
      const seriesClips = appState.clips.filter(c => c.storyId === clip.storyId);
      const completedInSeries = seriesClips.filter(c => isClipCompleted(c.id)).length;
      logEvent('series_progress_updated', {
        storyId: clip.storyId,
        completed: completedInSeries,
        total: seriesClips.length
      });
    }
    
    // Check if this clip was part of the daily set and if daily set is now complete
    if (appState.dailySet && appState.dailySet.includes(clipId)) {
      const completedCount = appState.dailySet.filter(id => isClipCompleted(id)).length;
      const totalCount = appState.dailySet.length;
      
      // Log clip completed in daily goal
      logEvent('daily_goal_clip_completed', {
        clipId: clipId,
        position: completedCount
      });
      
      const allDailySetComplete = completedCount === totalCount;
      
      if (allDailySetComplete) {
        // Calculate aggregate accuracy for session summary
        const aggregateStats = calculateDailySetAggregate();
        
        logEvent('daily_goal_completed', {
          goalSize: totalCount,
          completedCount: totalCount
        } as any); // Using any since we have extra fields
        
        // Render session summary after the current summary chip
        setTimeout(() => {
          renderSessionSummary(aggregateStats);
        }, 100);
      }
    }
  }
}

/**
 * Calculate aggregate accuracy for daily set
 */
function calculateDailySetAggregate(): { correct: number; total: number; percentage: number } {
  let totalCorrect = 0;
  let totalQuestions = 0;
  
  appState.dailySet.forEach((clipId: string) => {
    const perf = calculateClipPerformance(clipId) as ClipPerformance;
    totalCorrect += perf.correct;
    totalQuestions += perf.total;
  });
  
  const percentage = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
  
  return {
    correct: totalCorrect,
    total: totalQuestions,
    percentage: percentage
  };
}



/**
 * Load transcript visibility preference from localStorage
 */
export function loadTranscriptVisibility(): void {
  try {
    const stored = localStorage.getItem('listenUp_transcriptVisible');
    // Default to false if not set
    appState.transcriptVisible = stored === '1';
  } catch (e) {
    logger.error('Failed to load transcript visibility', e);
    appState.transcriptVisible = false;
  }
}

/**
 * Save transcript visibility preference to localStorage
 */
export function saveTranscriptVisibility(): void {
  try {
    localStorage.setItem('listenUp_transcriptVisible', appState.transcriptVisible ? '1' : '0');
  } catch (e) {
    logger.error('Failed to save transcript visibility', e);
  }
}
